import React, { useMemo, useState } from 'react';
import { OPS_STATE } from '../../../lib/statusApi';

/**
 * Platform operational state banner + rolling 90-day uptime history.
 *
 * Rendered INSIDE the existing Status & Updates page — this is not a standalone status application
 * and it adds no second incident-details system: drill-in reuses the event fields the SSU feed
 * already projects.
 *
 * FALSE-HEALTHY PROTECTION (Phase 2F): the green "All Systems Operational" treatment is shown ONLY
 * when the backend authoritatively reports `operational` AND the request completed cleanly AND the
 * surrounding feed is not degraded/partial. It is never inferred from "zero notices" — an empty feed
 * is not proof of health. Anything else renders the honest unknown/unavailable treatment.
 *
 * Day buckets arrive already resolved in the TENANT's local timezone (canonical storage stays UTC);
 * this component renders what the authority computed and never re-buckets by browser time.
 */

const STATE_UI = {
  [OPS_STATE.OPERATIONAL]: {
    label: 'Operational', bar: 'bg-emerald-500', chip: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  },
  [OPS_STATE.DEGRADED]: {
    label: 'Degraded performance', bar: 'bg-amber-500', chip: 'bg-amber-50 text-amber-800 border-amber-200',
  },
  [OPS_STATE.SCHEDULED_MAINTENANCE]: {
    // Distinguishable from degraded by hue AND by a striped treatment, so the two never rely on a
    // colour distinction alone.
    label: 'Scheduled maintenance', bar: 'bg-sky-500 uptime-bar--maintenance',
    chip: 'bg-sky-50 text-sky-800 border-sky-200',
  },
  [OPS_STATE.OUTAGE]: {
    label: 'Major incident', bar: 'bg-red-600', chip: 'bg-red-50 text-red-800 border-red-200',
  },
  [OPS_STATE.UNKNOWN]: {
    label: 'Status unavailable', bar: 'bg-gray-300', chip: 'bg-gray-50 text-gray-700 border-gray-200',
  },
};

const BANNER_COPY = {
  [OPS_STATE.OPERATIONAL]: 'All Systems Operational',
  [OPS_STATE.DEGRADED]: 'Degraded Performance',
  [OPS_STATE.SCHEDULED_MAINTENANCE]: 'Scheduled Maintenance In Progress',
  [OPS_STATE.OUTAGE]: 'Major Service Incident',
  [OPS_STATE.UNKNOWN]: 'Status Temporarily Unavailable',
};

function ui(state) {
  return STATE_UI[state] || STATE_UI[OPS_STATE.UNKNOWN];
}

export function formatUptime(pct) {
  if (typeof pct !== 'number' || Number.isNaN(pct)) return null;
  // Two decimals is the honest resolution for a 90-day window; never pad to a marketing "99.9%".
  return `${pct.toFixed(2)}%`;
}

function DayCell({ day, onSelect, selected }) {
  const meta = ui(day.state);
  const count = (day.events || []).length;
  const title = count
    ? `${day.date} — ${meta.label}: ${(day.events || []).map((e) => e.title).filter(Boolean).join('; ')}`
    : `${day.date} — ${meta.label}`;
  return (
    <button
      type="button"
      onClick={() => onSelect(selected ? null : day)}
      // Not colour alone: every cell carries an accessible label and a title tooltip, and the
      // state is exposed as a data attribute for assistive tech and tests.
      title={title}
      aria-label={title}
      data-state={day.state}
      data-date={day.date}
      data-testid="uptime-day"
      className={`uptime-day ${meta.bar} ${selected ? 'uptime-day--selected' : ''}`}
    />
  );
}

export default function UptimeHistory({ uptime, feedDegraded }) {
  const [selected, setSelected] = useState(null);

  // The banner may claim health ONLY on an authoritative, clean, non-degraded answer.
  const state = useMemo(() => {
    if (!uptime || uptime.ok !== true) return OPS_STATE.UNKNOWN;
    if (feedDegraded) return OPS_STATE.UNKNOWN;
    return uptime.state || OPS_STATE.UNKNOWN;
  }, [uptime, feedDegraded]);

  const healthy = state === OPS_STATE.OPERATIONAL;
  const days = (uptime && uptime.days) || [];
  const pct = formatUptime(uptime && uptime.uptimePercent);
  const meta = ui(state);

  return (
    <section className="mb-6" aria-label="Platform status" data-testid="uptime-history"
             data-ops-state={state}>
      <style>{`
        .uptime-day { width: 100%; height: 34px; border-radius: 2px; border: 0; padding: 0; cursor: pointer; }
        .uptime-day:focus-visible { outline: 2px solid #7c3aed; outline-offset: 2px; }
        .uptime-day--selected { outline: 2px solid #111827; outline-offset: 1px; }
        .uptime-bar--maintenance {
          background-image: repeating-linear-gradient(45deg, rgba(255,255,255,.45) 0 3px, transparent 3px 6px);
        }
        .uptime-grid { display: grid; grid-template-columns: repeat(90, minmax(0, 1fr)); gap: 2px; }
        @media (max-width: 640px) { .uptime-day { height: 26px; } .uptime-grid { gap: 1px; } }
        @media (prefers-reduced-motion: reduce) { .uptime-day { transition: none; } }
      `}</style>

      {/* CURRENT STATE BANNER — derived, never hardcoded. */}
      <div
        role="status"
        aria-live="polite"
        data-testid="ops-banner"
        data-healthy={healthy ? 'true' : 'false'}
        className={`rounded-lg border px-4 py-4 mb-4 flex items-center gap-3 ${
          healthy ? 'bg-emerald-50 border-emerald-200' : meta.chip}`}
      >
        <span
          aria-hidden="true"
          className={`inline-block rounded-full ${healthy ? 'bg-emerald-500' : meta.bar}`}
          style={{ width: 14, height: 14, flex: '0 0 auto' }}
        />
        <div className="min-w-0">
          <p className={`text-lg md:text-xl font-semibold ${healthy ? 'text-emerald-800' : ''}`}>
            {BANNER_COPY[state]}
          </p>
          {state === OPS_STATE.UNKNOWN ? (
            <p className="text-sm text-gray-600">
              We can&apos;t confirm platform status right now. Your workspace is unaffected.
            </p>
          ) : null}
        </div>
      </div>

      {/* 90-DAY HISTORY */}
      {uptime && uptime.ok && days.length ? (
        <div>
          <div className="flex items-baseline justify-between mb-2 gap-3 flex-wrap">
            <h3 className="text-sm font-semibold text-gray-800">Platform uptime</h3>
            {pct ? (
              <p className="text-sm text-gray-700" data-testid="uptime-percent">
                <span className="font-semibold">{pct}</span> uptime
                {uptime.counts && uptime.counts.scheduled_maintenance
                  ? ` · ${uptime.counts.scheduled_maintenance} maintenance window${
                      uptime.counts.scheduled_maintenance === 1 ? '' : 's'} (excluded)`
                  : ''}
              </p>
            ) : null}
          </div>

          <div className="uptime-grid" role="img"
               aria-label={`Platform status for the last ${days.length} days${pct ? `, ${pct} uptime` : ''}`}>
            {days.map((d) => (
              <DayCell key={d.date} day={d} selected={selected && selected.date === d.date}
                       onSelect={setSelected} />
            ))}
          </div>

          <div className="flex items-center justify-between mt-1 text-xs text-gray-500">
            <span>90 days ago</span>
            {uptime.timezone ? (
              <span className="hidden sm:inline" data-testid="uptime-timezone">
                Times shown in {uptime.timezone}
              </span>
            ) : null}
            <span>Today</span>
          </div>

          {/* DRILL-IN — reuses the fields the SSU feed already projects; no second details system. */}
          {selected ? (
            <div className="mt-3 border border-gray-200 rounded p-3 bg-gray-50" data-testid="uptime-drillin">
              <div className="flex items-center justify-between mb-1 gap-2">
                <p className="text-sm font-semibold text-gray-900">{selected.date}</p>
                <span className={`text-xs px-2 py-0.5 rounded border ${ui(selected.state).chip}`}>
                  {ui(selected.state).label}
                </span>
              </div>
              {(selected.events || []).length ? (
                <ul className="space-y-2">
                  {selected.events.map((e) => (
                    <li key={e.id} className="text-sm">
                      <p className="font-medium text-gray-900">{e.title}</p>
                      {e.summary ? <p className="text-gray-600">{e.summary}</p> : null}
                      <p className="text-xs text-gray-500">
                        {ui(e.state).label}
                        {e.severity ? ` · ${e.severity}` : ''}
                        {e.lifecycle ? ` · ${e.lifecycle}` : ''}
                      </p>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-gray-600">No incidents or maintenance on this day.</p>
              )}
              <button type="button" onClick={() => setSelected(null)}
                      className="mt-2 text-xs text-purple-600 hover:underline focus:outline-none focus:ring-2 focus:ring-purple-400">
                Close
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
